/**
 * RestoID — Enterprise SSO connector picker.
 * ---------------------------------------------------------------------------
 * RestoID frame around the PRISTINE upstream SSO connector list logic
 * (`useSingleSignOn`, reused `SocialLinkButton`s). Guard/redirect mirror the
 * upstream page. Swapped in via `src/App.tsx`.
 */
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import FirstScreenLayout from '@/Layout/FirstScreenLayout';
import PageContext from '@/Providers/PageContextProvider/PageContext';
import UserInteractionContext from '@/Providers/UserInteractionContextProvider/UserInteractionContext';
import SocialLinkButton from '@/components/Button/SocialLinkButton';
import useNativeMessageListener from '@/hooks/use-native-message-listener';
import useNavigateWithPreservedSearchParams from '@/hooks/use-navigate-with-preserved-search-params';
import useSingleSignOn from '@/hooks/use-single-sign-on';
import { getLogoUrl } from '@/shared/utils/logo';

import { ScreenHead, Stack } from '../components/restoid';
import { useCopy } from '../copy';

const SingleSignOnConnectors = () => {
  const { t } = useTranslation();
  const c = useCopy();
  const { theme } = useContext(PageContext);
  const { ssoEmail, ssoConnectors } = useContext(UserInteractionContext);
  const navigate = useNavigateWithPreservedSearchParams();
  const onSubmit = useSingleSignOn();

  const [loadingConnectorId, setLoadingConnectorId] = useState<string>();

  const handleSubmit = async (connectorId: string) => {
    setLoadingConnectorId(connectorId);
    await onSubmit(connectorId);
    setLoadingConnectorId(undefined);
  };

  useNativeMessageListener();

  useEffect(() => {
    if (!ssoEmail || ssoConnectors.length === 0) {
      navigate('../email', { replace: true });
    }
  }, [ssoEmail, navigate, ssoConnectors.length]);

  return (
    <FirstScreenLayout pageMeta={{ titleKey: 'action.single_sign_on' }}>
      <Stack gap={22}>
        <ScreenHead
          back={() => navigate(-1)}
          backLabel={c('backShort')}
          title={t('action.single_sign_on')}
          sub={t('description.single_sign_on_connectors_list', { email: ssoEmail })}
        />
        <Stack gap={12}>
          {ssoConnectors.map((connector) => {
            const { id, connectorName, logo: logoUrl, darkLogo: darkLogoUrl } = connector;
            return (
              <SocialLinkButton
                key={id}
                name={{ en: connectorName }}
                logo={getLogoUrl({ theme, logoUrl, darkLogoUrl })}
                target={connectorName}
                isLoading={loadingConnectorId === connector.id}
                onClick={() => {
                  void handleSubmit(connector.id);
                }}
              />
            );
          })}
        </Stack>
      </Stack>
    </FirstScreenLayout>
  );
};

export default SingleSignOnConnectors;
